import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Edit, Edit3, Trash2, Plus, Download, Upload, FileDown, Search, Copy,
  MoreVertical, Package, Calendar, Briefcase, Tag as TagIcon,
  FileText, Users, Heart, Grid3x3, List, CheckSquare, XSquare, RefreshCw,
  AlertTriangle, LayoutGrid
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { getFirstImage } from '../utils/imageHelper';
import ListingImage from '../components/ListingImage';
import { Label } from '../components/ui/label';
import StarRating from '../components/StarRating';
import { ListingsListLite } from '../components/lite';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { getFriendlyError } from '../utils/friendlyErrors';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const LISTING_TYPES = [
  { value: 'product', label: 'Product', icon: Package, color: 'bg-blue-100 text-blue-800' },
  { value: 'service', label: 'Service', icon: Briefcase, color: 'bg-green-100 text-green-800' },
  { value: 'rental', label: 'Rental', icon: Calendar, color: 'bg-purple-100 text-purple-800' },
  { value: 'catalog', label: 'Catalog', icon: LayoutGrid, color: 'bg-indigo-100 text-indigo-800' },
  { value: 'event', label: 'Event', icon: Calendar, color: 'bg-orange-100 text-orange-800' },
  { value: 'subscription', label: 'Subscription', icon: TagIcon, color: 'bg-pink-100 text-pink-800' },
  { value: 'classified', label: 'Classified', icon: FileText, color: 'bg-yellow-100 text-yellow-800' },
  { value: 'volunteering', label: 'Volunteering', icon: Heart, color: 'bg-red-100 text-red-800' }
];

const ListingsUnified = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { apiCall, user } = useAuth();
  const { formatPrice } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const [listings, setListings] = useState([]);
  const [searchMatches, setSearchMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated_desc');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // iter-221g — Bumped + tax-baked price waterfall (mirrors
  // ListingsListLite.getPrice()). The vendor card USED to render
  // listing.data.price (raw base) which made vendors think Shopify's
  // shipping bump was missing. The bump is computed server-side by
  // utils/listing_price.py and persisted as `aniyor_listing_price`.
  // Cards must display that — same number Shopify will charge — so the
  // vendor's portal is the source of truth for what the customer sees.
  const getDisplayPrice = (listing) => {
    return (
      listing.bumped_price ||
      listing.aniyor_listing_price ||
      listing.price ||
      listing.base_price ||
      listing?.data?.price ||
      0
    );
  };
  const getDisplayMrp = (listing) => {
    const d = listing?.data || {};
    return (
      d.compareAtPrice ||
      d.compare_at_price ||
      d.mrpPrice ||
      d.mrp_price ||
      listing.compare_at_price ||
      0
    );
  };
  
  // Mobile detection for Lite view
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Vendor filter state (admin only)
  const [vendorFilter, setVendorFilter] = useState(searchParams.get('vendor_id') || 'all');
  const [vendorName, setVendorName] = useState(searchParams.get('vendor_name') || '');
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  // Bulk selection state
  const [selectedListings, setSelectedListings] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({ category: '', sub_category: '', status: '', vendor_id: '', tags: '' });
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState({ items: [], format: '', csvContent: '', listingType: '' });
  const [importCategory, setImportCategory] = useState('');
  const [importSubCategory, setImportSubCategory] = useState('');
  const [importVendorId, setImportVendorId] = useState('');
  const [categories, setCategories] = useState([]);
  
  // Type selection dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState(null); // 'import', 'export', 'sample', 'import-catalogue'
  const [selectedType, setSelectedType] = useState('product');
  
  // HSN/SAC Alert state
  const [hsnAlertOpen, setHsnAlertOpen] = useState(false);
  const [pendingListingType, setPendingListingType] = useState(null);
  
  // Auto-refresh interval ref
  const refreshIntervalRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!file.name.endsWith('.csv')) {
      toast({ title: 'Invalid file', description: 'Please upload a CSV file', variant: 'destructive' });
      return;
    }
    setImporting(true);
    try {
      // Try multipart upload first
      const formData = new FormData();
      formData.append('file', file);
      let response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/listings/import/csv?listing_type=${selectedType}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` },
          body: formData
        }
      );

      // If Cloudflare WAF blocks (403), fall back to JSON-based import
      if (response.status === 403) {
        console.warn('[Import] Multipart blocked (403), falling back to JSON-based import');
        const arrayBuffer = await file.arrayBuffer();
        const base64 = btoa(
          new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
        );
        response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/listings/import/csv-json`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              csv_content: base64,
              listing_type: selectedType,
              filename: file.name,
            }),
          }
        );
      }

      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Import failed');
      toast({
        title: 'Import Complete',
        description: `Imported ${result.imported} ${selectedType} listing${result.imported !== 1 ? 's' : ''}${result.errors?.length ? `. ${result.errors.length} errors.` : '.'}`,
      });
      fetchAllListings();
    } catch (error) {
      toast({ title: 'Import Failed', description: getFriendlyError(error, 'Could not import listings. Please check your file and try again.'), variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  // Fetch vendors list for admin filter
  useEffect(() => {
    if (isAdmin) {
      fetchVendors();
    }
    fetchCategories();
  }, [isAdmin]);

  const fetchVendors = async () => {
    try {
      setLoadingVendors(true);
      const data = await apiCall('/api/vendors/');
      setVendors(data || []);
    } catch (error) {
      console.error('Failed to load vendors:', error);
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await apiCall('/api/categories/');
      setCategories(Array.isArray(data) ? data : data?.categories || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  useEffect(() => {
    fetchAllListings();
    
    // Set up auto-refresh every 30 seconds
    refreshIntervalRef.current = setInterval(() => {
      fetchAllListings(true); // silent refresh (no loading state)
    }, 30000);
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [vendorFilter]);

  // iter-411 — The baseline fetch above only loads the 200 MOST RECENTLY
  // CREATED listings per type-tab, then the search box below filters
  // client-side over that capped set. Any listing older than the 200th
  // newest of its type was therefore permanently invisible to search no
  // matter what was typed (reported bug: "Bhaula Godham A2 cow ghee"
  // missing from All Listings search). Fix: when a real search term is
  // typed, also ask the backend's own search (title/vendor_name/
  // vendor_store_name/sku/category/subcategory, already supported by
  // GET /api/listings/admin/all) and keep any matches it finds in a
  // SEPARATE `searchMatches` state (merged in getFilteredListings) — this
  // can only ADD items, never remove, and survives the periodic
  // background refresh of `listings`.
  useEffect(() => {
    if (!isAdmin || !searchQuery || searchQuery.startsWith('__')) {
      setSearchMatches([]);
      return;
    }
    const handle = setTimeout(() => {
      fetchSearchMatches(searchQuery);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchQuery, isAdmin, vendorFilter]);

  const fetchSearchMatches = async (query) => {
    try {
      const vendorParam = vendorFilter && vendorFilter !== 'all' ? `&vendor_id=${vendorFilter}` : '';
      const TABS = ['product', 'service', 'rental', 'event', 'subscription', 'classified', 'volunteering'];
      const tabResponses = await Promise.all(
        TABS.map(tab =>
          apiCall(`/api/listings/admin/all?tab=${tab}&limit=200&search=${encodeURIComponent(query)}${vendorParam}`).catch(() => ({ listings: [] }))
        )
      );
      const matches = tabResponses.flatMap(r => r?.listings || []);
      setSearchMatches(matches);
    } catch (_e) {
      // Non-critical — the existing client-side filter still works over
      // whatever was already loaded if this backend search call fails.
    }
  };

  const fetchAllListings = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      // iter-perf-1: Use unified admin endpoint — 1 HTTP request instead of 7.
      // Falls back to the old 7-call path on error so we never show a blank page.
      const vendorParam = vendorFilter && vendorFilter !== 'all' ? `&vendor_id=${vendorFilter}` : '';
      let allListings = [];

      try {
        // Step 1: fetch per-type counts (fast aggregation, no listing data)
        const countResp = await apiCall(`/api/listings/admin/all${vendorParam ? '?' + vendorParam.slice(1) : ''}`);
        if (countResp?.mode === 'counts') {
          // Step 2: fetch each tab in parallel using the new endpoint
          const TABS = ['product', 'service', 'rental', 'event', 'subscription', 'classified', 'volunteering'];
          const tabResponses = await Promise.all(
            TABS.map(tab =>
              apiCall(`/api/listings/admin/all?tab=${tab}&limit=200${vendorParam}`).catch(() => ({ listings: [] }))
            )
          );
          allListings = tabResponses.flatMap(r => r?.listings || []);
        }
      } catch (_e) {
        // Fallback: original 7-call path
        const responses = await Promise.all([
          apiCall(`/api/listings/?listing_type=product${vendorParam}`).catch(() => []),
          apiCall(`/api/listings/?listing_type=service${vendorParam}`).catch(() => []),
          apiCall(`/api/listings/?listing_type=rental${vendorParam}`).catch(() => []),
          apiCall(`/api/listings/?listing_type=event${vendorParam}`).catch(() => []),
          apiCall(`/api/listings/?listing_type=subscription${vendorParam}`).catch(() => []),
          apiCall(`/api/listings/?listing_type=classified${vendorParam}`).catch(() => []),
          apiCall(`/api/listings/?listing_type=volunteering${vendorParam}`).catch(() => []),
        ]);
        allListings = responses.flat();
      }

      setListings(allListings);
    } catch (error) {
      if (!silent) {
        toast({
          title: 'Error',
          description: 'Failed to load listings',
          variant: 'destructive'
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Helper function to calculate total inventory from variant combinations.
  // Mirrors the SSOT used by the Master Inventory page (InventoryNew.jsx)
  // — but hardened against a real data-divergence bug we've observed:
  // the Shopify inventory sync sometimes overwrites the snake_case
  // `variant_combinations[].stock` with 0 while the user-edited values
  // live untouched in `variantCombinations` (camel) and
  // `data.variant_combinations`. Reading only one path picks 0 and
  // reports the listing as out of stock even though the vendor has
  // genuine inventory. We therefore take the MAX sum across all 4
  // known locations so the card reflects the highest credible stock.
  const getTotalInventory = (listing) => {
    const sumStock = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return null;
      return arr.reduce((sum, vc) => sum + (Number(vc.stock) || Number(vc.inventory) || 0), 0);
    };

    const candidates = [
      sumStock(listing.variant_combinations),
      sumStock(listing.variantCombinations),
      sumStock(listing.data?.variantCombinations),
      sumStock(listing.data?.variant_combinations),
    ].filter((v) => v !== null);

    if (candidates.length > 0) {
      // Take the MAX — if one location says 0 (likely stale Shopify pull)
      // and another says 20 (user's real value), we trust the non-zero.
      return Math.max(...candidates);
    }

    // Subscription listings: capacity-style inventory
    const typeLower = (listing.listing_type || '').toLowerCase();
    if (typeLower === 'subscription' || typeLower === 'subscriptions') {
      return (
        listing.data?.inventory_available ||
        listing.data?.daily_capacity ||
        listing.data?.capacity_per_day ||
        listing.data?.capacity ||
        0
      );
    }

    // No variants — use base inventory
    return (
      listing.data?.inventory ||
      listing.data?.stock ||
      listing.data?.stock_quantity ||
      listing.data?.available_quantity ||
      listing.data?.ticketQuantity ||
      listing.data?.capacity ||
      0
    );
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      await apiCall(`/api/listings/${id}`, { method: 'DELETE' });
      setListings(listings.filter(l => l.id !== id));
      toast({
        title: 'Success',
        description: 'Listing deleted successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete listing',
        variant: 'destructive'
      });
    }
  };

  const handleDuplicate = async (listing, e) => {
    if (e) e.stopPropagation();
    try {
      const newListing = await apiCall(`/api/listings/${listing.id}/duplicate`, { method: 'POST' });
      if (newListing) {
        setListings(prev => [newListing, ...prev]);
        toast({
          title: 'Listing duplicated',
          description: `"${newListing.title}" saved as draft.`
        });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to duplicate listing', variant: 'destructive' });
    }
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedListings.size === displayListings.length) {
      setSelectedListings(new Set());
    } else {
      setSelectedListings(new Set(displayListings.map(l => l.id)));
    }
  };

  const toggleSelectListing = (id, e) => {
    e?.stopPropagation();
    const newSelected = new Set(selectedListings);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedListings(newSelected);
  };

  const clearSelection = () => {
    setSelectedListings(new Set());
  };

  // Bulk action handlers
  const handleBulkDelete = async () => {
    if (selectedListings.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedListings.size} listings?`)) return;
    
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedListings);
      await Promise.all(ids.map(id => 
        apiCall(`/api/listings/${id}`, { method: 'DELETE' }).catch(() => null)
      ));
      
      setListings(prev => prev.filter(l => !selectedListings.has(l.id)));
      setSelectedListings(new Set());
      
      toast({
        title: 'Success',
        description: `${ids.length} listings deleted`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Some listings failed to delete',
        variant: 'destructive'
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedListings.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedListings);
      let successCount = 0;
      let failedIds = [];
      
      for (const id of ids) {
        try {
          if (newStatus === 'pending') {
            // Vendor: Submit for approval via the dedicated endpoint
            await apiCall(`/api/listings/${id}/submit`, { method: 'POST' });
          } else if (newStatus === 'active' && isAdmin) {
            // Admin: Direct publish via the submit endpoint (admin bypasses approval)
            await apiCall(`/api/listings/${id}/submit`, { method: 'POST' });
          } else if (newStatus === 'draft' && !isAdmin) {
            // Vendor unpublish: dedicated endpoint bypasses invisible-forking
            await apiCall(`/api/listings/${id}/unpublish`, { method: 'POST' });
          } else {
            // Other status changes (admin draft, etc.)
            await apiCall(`/api/listings/${id}`, {
              method: 'PUT',
              body: JSON.stringify({ status: newStatus })
            });
          }
          successCount++;
        } catch (e) {
          console.error(`Failed to update listing ${id}:`, e);
          failedIds.push(id);
        }
      }
      
      // Refresh listings
      await fetchAllListings();
      setSelectedListings(new Set());
      
      const failedMsg = failedIds.length > 0 ? ` (${failedIds.length} failed — may need validation)` : '';
      toast({
        title: successCount > 0 ? 'Success' : 'Failed',
        description: newStatus === 'pending' && !isAdmin
          ? `${successCount} listing${successCount !== 1 ? 's' : ''} submitted for approval${failedMsg}`
          : newStatus === 'pending' || newStatus === 'active'
          ? `${successCount} listing${successCount !== 1 ? 's' : ''} published${failedMsg}`
          : `${successCount} listing${successCount !== 1 ? 's' : ''} set to ${newStatus}${failedMsg}`,
        variant: failedIds.length > 0 && successCount === 0 ? 'destructive' : undefined
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update listings',
        variant: 'destructive'
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk Edit handler
  const handleBulkEdit = async () => {
    if (selectedListings.size === 0) return;
    setBulkActionLoading(true);
    try {
      const payload = { listing_ids: Array.from(selectedListings) };
      if (bulkEditData.category) payload.category = bulkEditData.category;
      if (bulkEditData.sub_category) payload.sub_category = bulkEditData.sub_category;
      if (bulkEditData.status && isAdmin) payload.status = bulkEditData.status;
      if (bulkEditData.vendor_id && isAdmin) payload.vendor_id = bulkEditData.vendor_id;
      if (bulkEditData.tags) payload.tags = bulkEditData.tags.split(',').map(t => t.trim()).filter(Boolean);

      const result = await apiCall('/api/listings/bulk-update', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      toast({ title: 'Bulk Update', description: `${result.updated} listing(s) updated` });
      setBulkEditOpen(false);
      setBulkEditData({ category: '', sub_category: '', status: '', vendor_id: '', tags: '' });
      setSelectedListings(new Set());
      fetchAllListings();
    } catch (error) {
      toast({ title: 'Bulk update failed', description: getFriendlyError(error), variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Import with preview
  const handleImportWithPreview = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!file.name.endsWith('.csv')) {
      toast({ title: 'Invalid file', description: 'Please upload a CSV file', variant: 'destructive' });
      return;
    }
    try {
      const arrayBuffer = await file.arrayBuffer();
      const base64 = btoa(new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''));
      const result = await apiCall('/api/listings/import/preview', {
        method: 'POST',
        body: JSON.stringify({ csv_content: base64, listing_type: selectedType }),
      });
      if (result.items?.length > 0) {
        setImportPreviewData({ items: result.items.map(i => ({ ...i, selected: true })), format: result.format, csvContent: base64, listingType: selectedType });
        setImportCategory('');
        setImportSubCategory('');
        setImportVendorId('');
        setImportPreviewOpen(true);
      } else {
        toast({ title: 'No items found', description: 'The CSV file has no importable items.', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Preview failed', description: getFriendlyError(error), variant: 'destructive' });
    }
  };

  const confirmImport = async () => {
    setImporting(true);
    setImportPreviewOpen(false);
    try {
      // Only import selected items
      const selectedItems = importPreviewData.items.filter(i => i.selected);
      if (selectedItems.length === 0) {
        toast({ title: 'No items selected', description: 'Please select at least one item to import.', variant: 'destructive' });
        setImporting(false);
        return;
      }
      const selectedTitles = selectedItems.map(i => i.title || i.Title || i.name || '');

      let response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/listings/import/csv-json`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            csv_content: importPreviewData.csvContent,
            listing_type: importPreviewData.listingType,
            selected_titles: selectedTitles,
            vendor_id: (isAdmin && importVendorId) ? importVendorId : undefined,
          }),
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Import failed');

      // Apply category/subcategory to imported items if specified.
      // iter-300 — vendor_id is now assigned atomically by the import
      // endpoint itself (above), not via this fragile post-hoc guess of
      // "most recently created N listings" (which risked reassigning
      // unrelated listings created in the same window).
      if (importCategory || importSubCategory) {
        const allListings = await apiCall('/api/listings/?limit=500&sort=-created_at');
        const listingsList = Array.isArray(allListings) ? allListings : allListings?.listings || [];
        const recentIds = listingsList.slice(0, result.imported).map(l => l.id);
        if (recentIds.length > 0) {
          const updatePayload = { listing_ids: recentIds };
          if (importCategory) updatePayload.category = importCategory;
          if (importSubCategory) updatePayload.sub_category = importSubCategory;
          await apiCall('/api/listings/bulk-update', { method: 'PUT', body: JSON.stringify(updatePayload) });
        }
      }

      toast({
        title: 'Import Complete',
        description: `Imported ${result.imported} listing(s)${result.errors?.length ? `. ${result.errors.length} errors.` : '.'}`,
      });
      fetchAllListings();
    } catch (error) {
      toast({ title: 'Import Failed', description: getFriendlyError(error), variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const togglePreviewItem = (index) => {
    setImportPreviewData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, selected: !item.selected } : item),
    }));
  };

  const getSelectedSubCategories = (catName) => {
    const cat = categories.find(c => c.name === catName);
    return cat?.subcategories || cat?.sub_categories || [];
  };

  // iter-331 — Compute whether a listing is missing Country of Origin or
  // its category-mandated license, computed client-side against the
  // already-loaded `categories` list (no extra API call needed). Used to
  // flag pre-existing listings created before these fields existed.
  const getComplianceGap = (listing) => {
    const gaps = [];
    const cat = categories.find(c => c.name === listing.category);
    const sub = (cat?.subcategories || cat?.sub_categories || []).find(s => s.name === listing.subcategory);
    const pick = (field) => (sub?.[field] ?? cat?.[field]);
    const requiredType = pick('required_license_type');
    // iter-364 — Admin decides, per category/subcategory, whether ANY
    // compliance rule applies (mirrors backend utils/listing_compliance.py
    // validate_compliance_fields). Country of Origin used to be flagged
    // unconditionally here, so every Service/Event listing (which never
    // has this CMS-driven compliance activated) permanently showed
    // "Needs Compliance Info" with no way to clear it.
    const complianceActivated = !!(requiredType || pick('enable_expiry_rules') || pick('enable_warranty_rules'));
    if (!complianceActivated) return gaps;
    if (!listing.country_of_origin || !String(listing.country_of_origin).trim()) {
      gaps.push('Country of Origin');
    }
    if (requiredType) {
      const matched = (listing.licenses_certifications || []).some(
        l => l.type === requiredType && (l.number || '').trim()
      );
      if (!matched) gaps.push(`${requiredType} License`);
    }
    // iter-332 — Shelf-Life/Expiry gap
    if (pick('enable_expiry_rules')) {
      const fmt = pick('expiry_date_format') || 'shelf_life';
      const sl = listing.shelf_life || {};
      const missing = fmt === 'exact_date' ? !sl.expiry_date : (sl.shelf_life_days === null || sl.shelf_life_days === undefined);
      if (missing) gaps.push(pick('expiry_legal_label') || 'Shelf Life');
    }
    // iter-332 — Warranty/Guarantee gap
    if (pick('enable_warranty_rules')) {
      const w = listing.warranty || {};
      if (!w.duration_value) gaps.push('Warranty/Guarantee');
    }
    return gaps;
  };

  const getFilteredCategories = () => {
    // Group categories by listing type matching current selectedType
    const typeMap = { product: 'product', service: 'service', rental: 'rental', subscription: 'subscription', event: 'event', classified: 'classified', volunteering: 'volunteering' };
    const activeType = typeMap[selectedType?.toLowerCase()] || selectedType?.toLowerCase();
    if (!activeType) return categories;
    const matched = categories.filter(c => c.listing_types?.includes(activeType));
    return matched.length > 0 ? matched : categories;
  };

  const handleBulkShopifySync = async () => {
    if (selectedListings.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedListings);
      const result = await apiCall('/api/shopify-sync/bulk-push', {
        method: 'POST',
        body: JSON.stringify({ listing_ids: ids })
      });
      
      // Refresh listings
      await fetchAllListings();
      setSelectedListings(new Set());
      
      toast({
        title: 'Shopify Sync',
        description: `${result.success || 0} synced, ${result.failed || 0} failed`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to sync to Shopify',
        variant: 'destructive'
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const openDialog = (action) => {
    setDialogAction(action);
    setDialogOpen(true);
  };

  const handleDialogConfirm = async () => {
    setDialogOpen(false);
    
    switch (dialogAction) {
      case 'import':
        if (selectedType === 'product' && dialogAction === 'import-catalogue') {
          navigate('/catalogue-pull');
        } else {
          fileInputRef.current?.click();
        }
        break;
      case 'export':
        await handleExport(selectedType);
        break;
      case 'sample':
        await handleDownloadSample(selectedType);
        break;
    }
  };

  const handleExport = async (type) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/listings/export/csv?listing_type=${type}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-listings.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Success',
        description: `${type} listings exported successfully`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to export listings',
        variant: 'destructive'
      });
    }
  };

  const handleDownloadSample = async (type) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/listings/sample/csv`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-sample.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Success',
        description: 'Sample template downloaded'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to download sample',
        variant: 'destructive'
      });
    }
  };

  const getFilteredListings = () => {
    // iter-411 fix-2 — `searchMatches` is kept SEPARATE from `listings` so
    // the 30s auto-refresh (which calls setListings(...) and would
    // otherwise wipe out matches found beyond the normal per-tab cap)
    // can never erase them. Union (deduped by id) is recomputed fresh
    // every time this runs.
    const byId = new Map(listings.map(l => [l.id, l]));
    searchMatches.forEach(l => byId.set(l.id, l));
    let filtered = Array.from(byId.values());

    // Type filter — DB stores plural ("Subscriptions", "Products") but the
    // filter UI uses singular slugs ("subscription", "product"). Normalise
    // both sides so the equality check works for either form.
    if (typeFilter !== 'all') {
      const normalize = (s) => {
        const v = String(s || '').toLowerCase().trim();
        if (!v) return '';
        if (v.endsWith('ies')) return v.slice(0, -3) + 'y';
        if (v.endsWith('s') && v.length > 4) return v.slice(0, -1);
        return v;
      };
      const target = normalize(typeFilter);
      filtered = filtered.filter(l => normalize(l.listing_type) === target);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(l => l.status === statusFilter);
    }

    // Source filter (imported vs manual)
    if (sourceFilter === 'imported') {
      filtered = filtered.filter(l => l.shopify_handle);
    } else if (sourceFilter === 'manual') {
      filtered = filtered.filter(l => !l.shopify_handle);
    }

    // Search filter — multi-field substring match. Looks at the listing's
    // title/description/category/handle/SKU/listing_type/status AND, for
    // admins, the vendor's display + business name + email. Multi-word
    // queries are AND-joined (every token must match somewhere) so that
    // "saree red" finds "Red Cotton Saree" without needing a backend
    // search index. iter-226 — previously a single .includes() that only
    // matched title/description/category and silently ignored vendor /
    // SKU / handle queries (user reported "search not robust").
    if (searchQuery) {
      // iter-215 P1 — special sentinel to filter only sync-flagged listings
      if (searchQuery === '__sync_flagged__') {
        filtered = filtered.filter(l => l.sync_warning || l.sync_paused);
      } else if (searchQuery === '__compliance_gap__') {
        // iter-331 — special sentinel to filter only listings missing
        // Country of Origin or their category-mandated license.
        filtered = filtered.filter(l => getComplianceGap(l).length > 0);
      } else {
        const tokens = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
        const vendorById = isAdmin && vendors.length > 0
          ? Object.fromEntries(vendors.map(v => [v.id, v]))
          : null;
        filtered = filtered.filter(l => {
          const haystackParts = [
            l.title,
            l.description,
            l.category,
            l.subcategory,
            l.listing_type,
            l.status,
            l.shopify_handle,
            l.handle,
            l.sku,
            l.vendor_name,
            l.vendor_store_name,
            ...(Array.isArray(l.variants) ? l.variants.map(v => v?.sku) : []),
          ];
          if (vendorById) {
            const vendor = vendorById[l.vendor_id];
            if (vendor) {
              haystackParts.push(
                vendor.name,
                vendor.email,
                vendor?.vendor_profile?.business_details?.business_name,
              );
            }
          }
          // Also pull a few likely-searchable fields from the nested data blob
          const data = l.data || {};
          haystackParts.push(data.sku, data.brand, data.title, data.eventName, data.venueName);
          const haystack = haystackParts.filter(Boolean).join(' • ').toLowerCase();
          return tokens.every(tok => haystack.includes(tok));
        });
      }
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'updated_desc':
          return new Date(b.updated_at) - new Date(a.updated_at);
        case 'updated_asc':
          return new Date(a.updated_at) - new Date(b.updated_at);
        case 'created_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        default:
          return 0;
      }
    });

    return filtered;
  };

  const getTypeInfo = (type) => {
    // Normalize type to lowercase and singular for comparison
    const normalizedType = type?.toLowerCase().replace(/s$/, '') || 'product';
    return LISTING_TYPES.find(t => t.value === normalizedType || t.value === type?.toLowerCase()) || LISTING_TYPES[0];
  };

  const getTypeRoute = (type, id, action = 'view') => {
    // Normalize type to lowercase and singular
    const normalizedType = type?.toLowerCase().replace(/s$/, '') || 'product';

    // Catalog has no dedicated list/details page yet — route straight into
    // the existing Phase 1 schema-driven create/edit flow.
    if (normalizedType === 'catalog') {
      if (action === 'new') return '/create-listing/catalog';
      return `/edit-listing/catalog/${id}`;
    }

    const baseRoutes = {
      product: '/listings/products',
      service: '/listings/services',
      rental: '/listings/rentals',
      event: '/listings/events',
      subscription: '/listings/subscriptions',
      classified: '/listings/classifieds',
      volunteering: '/listings/volunteering'
    };
    
    const base = baseRoutes[normalizedType] || baseRoutes[type?.toLowerCase()] || '/listings/products';
    
    if (action === 'new') return `${base}/new`;
    if (action === 'edit') return `${base}/edit/${id}`;
    return `${base}/${id}`;
  };

  // Get existing listing types from current listings
  const getExistingListingTypes = () => {
    const types = new Set(listings.map(l => l.listing_type));
    return Array.from(types);
  };

  // Check if vendor has listings of a different type and show HSN/SAC alert
  const handleCreateListing = (newType) => {
    const existingTypes = getExistingListingTypes();
    
    // If vendor has existing listings and the new type is different
    if (existingTypes.length > 0 && !existingTypes.includes(newType)) {
      setPendingListingType(newType);
      setHsnAlertOpen(true);
      return;
    }
    
    // No conflict, navigate directly
    navigate(getTypeRoute(newType, null, 'new'));
  };

  // Proceed with creating listing after HSN/SAC acknowledgment
  const handleHsnAlertConfirm = () => {
    setHsnAlertOpen(false);
    if (pendingListingType) {
      navigate(getTypeRoute(pendingListingType, null, 'new'));
      setPendingListingType(null);
    }
  };

  // Cancel HSN/SAC alert
  const handleHsnAlertCancel = () => {
    setHsnAlertOpen(false);
    setPendingListingType(null);
  };

  const displayListings = getFilteredListings();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading listings...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6 max-w-full overflow-hidden">
        {/* iter-215 P1 — sync-warning rollup banner. Shows a single red
            strip at the top of the Listings page summarising how many
            listings the Shopify scheduler has flagged (variant drops or
            circuit-breaker pauses). Click to filter to only those. */}
        {(() => {
          const warned = listings.filter(l => l.sync_warning || l.sync_paused);
          if (warned.length === 0) return null;
          const paused = warned.filter(l => l.sync_paused).length;
          return (
            <div
              className="rounded-lg border-l-4 border-red-500 bg-red-50 p-4 flex items-start gap-3"
              data-testid="sync-warning-banner"
            >
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-semibold text-red-900">
                  Shopify sync flagged {warned.length} listing{warned.length === 1 ? '' : 's'}
                  {paused > 0 && ` — ${paused} auto-paused after 3 failed pushes`}
                </p>
                <p className="text-red-800 mt-0.5">
                  {paused > 0
                    ? 'Open each flagged listing and click "Re-sync to Shopify" to reset the circuit breaker.'
                    : 'Some variants may have been dropped during the last scheduled push. Review the flagged listings below.'}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-red-700 border-red-300 hover:bg-red-100"
                onClick={() => setSearchQuery('__sync_flagged__')}
                data-testid="sync-warning-banner-filter"
              >
                Filter flagged
              </Button>
            </div>
          );
        })()}

        {/* iter-331 — Compliance gap rollup banner. Flags pre-existing
            listings missing Country of Origin or their category-mandated
            license (FSSAI/BIS/CDSCO) — visible to both admin and vendor. */}
        {(() => {
          const gapped = listings.filter(l => getComplianceGap(l).length > 0);
          if (gapped.length === 0) return null;
          return (
            <div
              className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4 flex items-start gap-3"
              data-testid="compliance-gap-banner"
            >
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-semibold text-amber-900">
                  {gapped.length} listing{gapped.length === 1 ? '' : 's'} need{gapped.length === 1 ? 's' : ''} compliance info
                </p>
                <p className="text-amber-800 mt-0.5">
                  Missing Country of Origin and/or a legally-required license number (FSSAI / BIS / CDSCO).
                  Open and re-save each flagged listing to add it.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-amber-700 border-amber-300 hover:bg-amber-100"
                onClick={() => setSearchQuery('__compliance_gap__')}
                data-testid="compliance-gap-banner-filter"
              >
                Filter flagged
              </Button>
            </div>
          );
        })()}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Listings</h1>
            <p className="text-sm md:text-base text-gray-600 mt-1">Manage all your listings in one place</p>
          </div>
          
          <div className="flex gap-2">
            {/* View Mode Toggle - Desktop Only */}
            <div className="hidden md:flex border rounded-lg">
              <Button 
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            {/* Add Listing Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="flex-1 md:flex-none">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Listing
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {LISTING_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <DropdownMenuItem
                      key={type.value}
                      onClick={() => handleCreateListing(type.value)}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      Add {type.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* More Actions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex-1 md:flex-none">
                  <MoreVertical className="h-4 w-4 md:mr-2" />
                  <span className="hidden md:inline">More Actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => openDialog('import')}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Listings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openDialog('export')}>
                  <Download className="h-4 w-4 mr-2" />
                  Export Listings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openDialog('sample')}>
                  <FileDown className="h-4 w-4 mr-2" />
                  Download Sample CSV
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/catalogue-pull')}>
                  <Package className="h-4 w-4 mr-2" />
                  Import Catalogue (Products)
                </DropdownMenuItem>
                {selectedType === 'product' && (
                  <DropdownMenuItem onClick={() => navigate('/bulk-product-editor')} data-testid="more-actions-bulk-editor">
                    <Grid3x3 className="h-4 w-4 mr-2" />
                    Bulk Edit Products (Spreadsheet)
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Filters - Hidden on mobile (Lite Mode) */}
        <Card className={`max-w-full overflow-hidden ${isMobile ? 'hidden' : ''}`}>
          <CardContent className="p-3 md:p-6">
            <div className="flex flex-col gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  data-testid="listings-search-input"
                  placeholder={isAdmin ? "Search by title, category, or vendor..." : "Search listings..."}
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Filters Row */}
              <div className={`grid ${isAdmin ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3'} gap-2`}>
                {/* Vendor Filter (Admin only) */}
                {isAdmin && (
                  <Select 
                    value={vendorFilter} 
                    onValueChange={(val) => {
                      setVendorFilter(val);
                      // Update URL params
                      if (val && val !== 'all') {
                        const vendor = vendors.find(v => v.id === val);
                        setSearchParams({ vendor_id: val, vendor_name: vendor?.name || '' });
                        setVendorName(vendor?.name || '');
                      } else {
                        setSearchParams({});
                        setVendorName('');
                      }
                    }}
                  >
                    <SelectTrigger className="text-xs">
                      <Users className="h-3 w-3 mr-1" />
                      <SelectValue placeholder="All Vendors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Vendors</SelectItem>
                      {vendors.map(vendor => (
                        <SelectItem key={vendor.id} value={vendor.id}>
                          {vendor.vendor_profile?.store_name || vendor.vendor_profile?.business_details?.business_name || vendor.name || vendor.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                
                {/* Type Filter */}
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="text-xs">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {LISTING_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Status Filter */}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="text-xs">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="pending_approval">Pending</SelectItem>
                  </SelectContent>
                </Select>

                {/* Sort */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="text-xs">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated_desc">Last Updated</SelectItem>
                    <SelectItem value="created_desc">Recently Created</SelectItem>
                    <SelectItem value="updated_asc">Oldest</SelectItem>
                    <SelectItem value="title">Title</SelectItem>
                  </SelectContent>
                </Select>

                {/* Source filter */}
                {isAdmin && (
                  <Select value={sourceFilter} onValueChange={setSourceFilter}>
                    <SelectTrigger className="text-xs">
                      <SelectValue placeholder="Source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="imported">Imported</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vendor Filter Active Banner */}
        {isAdmin && vendorFilter && vendorFilter !== 'all' && vendorName && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-blue-800">
                Showing listings for <strong>{vendorName}</strong>
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                setVendorFilter('all');
                setVendorName('');
                setSearchParams({});
              }}
              className="text-blue-600 hover:text-blue-800"
            >
              Clear Filter
            </Button>
          </div>
        )}

        {/* Bulk Actions Bar - Hidden on mobile (Lite Mode) */}
        {selectedListings.size > 0 && !isMobile && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <CheckSquare className="h-5 w-5 text-indigo-600" />
              <span className="text-sm font-medium text-indigo-800">
                {selectedListings.size} listing{selectedListings.size > 1 ? 's' : ''} selected
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearSelection}
                className="text-indigo-600 hover:text-indigo-800"
              >
                <XSquare className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setBulkEditData({ category: '', sub_category: '', status: '', vendor_id: '', tags: '' });
                  setBulkEditOpen(true);
                }}
                disabled={bulkActionLoading}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                <Edit3 className="h-4 w-4 mr-1" />
                Bulk Edit
              </Button>
              {selectedType === 'product' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const productIds = Array.from(selectedListings).filter(id => {
                      const listing = listings.find(l => l.id === id);
                      return !listing || listing.listing_type === 'product';
                    });
                    const skipped = selectedListings.size - productIds.length;
                    if (productIds.length === 0) {
                      toast({ title: 'No products selected', description: 'Bulk Edit (Spreadsheet) only works on product listings.', variant: 'destructive' });
                      return;
                    }
                    if (skipped > 0) {
                      toast({ title: `${skipped} non-product listing${skipped > 1 ? 's' : ''} skipped`, description: 'The spreadsheet editor only supports products.' });
                    }
                    navigate(`/bulk-product-editor?ids=${productIds.join(',')}`);
                  }}
                  disabled={bulkActionLoading}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid="bulk-editor-spreadsheet-btn"
                >
                  <Grid3x3 className="h-4 w-4 mr-1" />
                  Bulk Edit (Spreadsheet)
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleBulkStatusChange(isAdmin ? 'active' : 'pending')}
                disabled={bulkActionLoading}
                className="border-green-300 text-green-700 hover:bg-green-50"
                data-testid="bulk-publish-btn"
              >
                {isAdmin ? 'Publish' : 'Submit for Approval'}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleBulkStatusChange('draft')}
                disabled={bulkActionLoading}
                className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
              >
                Unpublish
              </Button>
              {isAdmin && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleBulkShopifySync}
                  disabled={bulkActionLoading}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Sync to Shopify
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleBulkDelete}
                disabled={bulkActionLoading}
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        )}

        {/* Listings - Grid View (Desktop Only) */}
        <div className={`${viewMode === 'grid' ? 'hidden md:grid' : 'hidden'} md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4`}>
            {displayListings.length > 0 ? displayListings.map((listing) => {
              const typeInfo = getTypeInfo(listing.listing_type);
              const TypeIcon = typeInfo.icon;
              
              return (
                <Card 
                  key={listing.id} 
                  className={`cursor-pointer hover:shadow-md transition-shadow overflow-hidden relative ${selectedListings.has(listing.id) ? 'ring-2 ring-indigo-500' : ''}`}
                  onClick={() => navigate(getTypeRoute(listing.listing_type, listing.id))}
                >
                  {/* Checkbox - Large click area */}
                  <div 
                    className="absolute top-0 left-0 z-10 p-3 cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); toggleSelectListing(listing.id); }}
                  >
                    <Checkbox
                      checked={selectedListings.has(listing.id)}
                      className="h-5 w-5 bg-white/80 border-2 pointer-events-none"
                    />
                  </div>
                  
                  {/* Image with fallback */}
                  <ListingImage
                    src={getFirstImage(listing.media || listing.images)}
                    alt={listing.title}
                    className="w-full h-48 object-cover"
                    placeholderContent={<TypeIcon className="h-12 w-12 text-gray-400" />}
                  />
                  
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2 gap-2">
                      <h3 className="font-semibold text-base line-clamp-1 flex-1">
                        {listing.title}
                      </h3>
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <Badge className={`${typeInfo.color} text-xs`}>
                          {typeInfo.label}
                        </Badge>
                        <Badge 
                          variant={listing.status === 'active' ? 'default' : 'secondary'}
                          className={`text-xs ${listing.status === 'inactive' ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' : ''}`}
                        >
                          {listing.status === 'active' ? 'Active' : listing.status === 'draft' ? 'Draft' : listing.status === 'inactive' ? 'Inactive' : 'Pending'}
                        </Badge>
                        {/* iter-215 P1 — sync_warning badge: visible red chip
                            when the nightly Shopify sync dropped variants or
                            the 3-strike circuit breaker tripped. */}
                        {(listing.sync_paused || listing.sync_warning) && (
                          <Badge
                            variant="destructive"
                            className="text-[10px] px-1.5 py-0.5 cursor-help"
                            title={
                              listing.sync_paused
                                ? `Sync paused — ${listing.sync_paused_reason || 'too many failures'}`
                                : (listing.sync_warning?.message || 'Shopify sync warning')
                            }
                            data-testid={`listing-sync-warning-${listing.id}`}
                          >
                            ⚠ Sync
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {isAdmin && (() => {
                      const v = vendors.find(vd => vd.id === listing.vendor_id);
                      const vName = listing.vendor_store_name || listing.vendor_name || v?.vendor_profile?.store_name || v?.vendor_profile?.business_details?.business_name || v?.name;
                      return vName ? (
                        <p className="text-xs text-blue-600 font-medium mb-1 truncate">Seller: {vName}</p>
                      ) : null;
                    })()}
                    
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {listing.description ? listing.description.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').substring(0, 150) : ''}
                    </p>
                    
                    <div className="flex justify-between items-center mb-2">
                      {(() => {
                        const displayPrice = getDisplayPrice(listing);
                        const displayMrp = getDisplayMrp(listing);
                        if (!displayPrice) return null;
                        return (
                          <span className="flex items-baseline gap-2">
                            <span className="text-lg font-bold">{formatPrice(displayPrice)}</span>
                            {displayMrp > displayPrice && (
                              <span className="text-xs line-through text-gray-400">{formatPrice(displayMrp)}</span>
                            )}
                          </span>
                        );
                      })()}
                      {(listing.listing_type === 'product' || listing.data?.inventory) && (
                        <span className="text-xs text-gray-500">
                          Stock: {getTotalInventory(listing)}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex justify-between items-center">
                      {listing.category && (
                        <Badge variant="outline" className="text-xs">
                          {listing.category}
                        </Badge>
                      )}
                      {getComplianceGap(listing).length > 0 && (
                        <Badge
                          className="bg-red-100 text-red-800 text-xs"
                          data-testid={`compliance-gap-badge-grid-${listing.id}`}
                          title={`Missing: ${getComplianceGap(listing).join(', ')}`}
                        >
                          Needs Compliance Info
                        </Badge>
                      )}
                      <div className="flex gap-1">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          title="Duplicate listing"
                          data-testid={`duplicate-listing-grid-${listing.id}`}
                          onClick={(e) => handleDuplicate(listing, e)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            navigate(getTypeRoute(listing.listing_type, listing.id, 'edit')); 
                          }}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            handleDelete(listing.id); 
                          }}
                        >
                          <Trash2 className="h-3 w-3 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            }) : (
              <Card className="col-span-full">
                <CardContent className="p-8 text-center">
                  <p className="text-gray-500 mb-4">No listings found</p>
                  <Button onClick={() => navigate('/create-listing')} data-testid="add-first-listing-grid">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Listing
                  </Button>
                </CardContent>
              </Card>
            )}
        </div>

        {/* Listings - List View (Always on Mobile, Optional on Desktop) */}
        <div className={`${viewMode === 'list' ? 'block' : 'md:hidden'}`}>
          {/* Mobile Lite View */}
          {isMobile ? (
            <Card>
              <CardContent className="p-0">
                <ListingsListLite 
                  listings={displayListings}
                  loading={loading}
                  onToggleStock={async (listing, inStock) => {
                    try {
                      await apiCall(`/api/listings/${listing.id}/status`, {
                        method: 'PUT',
                        body: JSON.stringify({ status: inStock ? 'active' : 'out_of_stock' })
                      });
                      toast({ 
                        title: inStock ? 'In Stock' : 'Out of Stock', 
                        description: `${listing.title} is now ${inStock ? 'available' : 'out of stock'}` 
                      });
                      fetchAllListings();
                    } catch (error) {
                      toast({ title: 'Error', description: 'Failed to update stock status', variant: 'destructive' });
                    }
                  }}
                />
              </CardContent>
            </Card>
          ) : (
            /* Desktop List View */
            <div className="space-y-3">
              {/* Select All for List View */}
              {displayListings.length > 0 && (
                <div className="flex items-center gap-2 px-2">
                  <Checkbox
                    checked={selectedListings.size === displayListings.length && displayListings.length > 0}
                    onCheckedChange={toggleSelectAll}
                    className="h-5 w-5"
                  />
                  <span className="text-sm text-muted-foreground">
                    Select All ({displayListings.length})
                  </span>
                </div>
              )}
              
              {displayListings.length > 0 ? displayListings.map((listing) => {
                const typeInfo = getTypeInfo(listing.listing_type);
                const TypeIcon = typeInfo.icon;
                
                return (
                  <Card 
                    key={listing.id} 
                    className={`cursor-pointer hover:shadow-md transition-shadow max-w-full overflow-hidden ${selectedListings.has(listing.id) ? 'ring-2 ring-indigo-500' : ''}`}
                    onClick={() => navigate(getTypeRoute(listing.listing_type, listing.id))}
                  >
                    <CardContent className="p-3 md:p-4">
                      <div className="flex gap-3">
                        {/* Checkbox - Large click area */}
                        <div 
                          className="flex items-start pt-1 pr-1 cursor-pointer"
                          onClick={(e) => { e.stopPropagation(); toggleSelectListing(listing.id); }}
                        >
                          <Checkbox
                            checked={selectedListings.has(listing.id)}
                            className="h-5 w-5 pointer-events-none"
                          />
                        </div>
                        
                        {/* Image with fallback */}
                        <ListingImage
                          src={getFirstImage(listing.media || listing.images)}
                          alt={listing.title}
                          className="w-16 h-16 md:w-20 md:h-20 object-cover rounded flex-shrink-0"
                          placeholderContent={<TypeIcon className="h-6 w-6 text-gray-400" />}
                        />
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-1 gap-2">
                            <h3 className="font-semibold text-sm md:text-base line-clamp-1 flex-1">
                              {listing.title}
                            </h3>
                            <div className="flex gap-1 flex-shrink-0">
                              <Badge className={`${typeInfo.color} text-xs`}>
                                {typeInfo.label}
                              </Badge>
                              <Badge 
                                variant={listing.status === 'active' ? 'default' : 'secondary'}
                                className={`text-xs ${listing.status === 'inactive' ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' : ''}`}
                              >
                                {listing.status === 'active' ? 'Active' : listing.status === 'draft' ? 'Draft' : listing.status === 'inactive' ? 'Inactive' : 'Pending'}
                              </Badge>
                            </div>
                          </div>
                          
                          <div className="mb-1">
                            {isAdmin && (() => {
                              const v = vendors.find(vd => vd.id === listing.vendor_id);
                              const vName = listing.vendor_store_name || listing.vendor_name || v?.vendor_profile?.store_name || v?.vendor_profile?.business_details?.business_name || v?.name;
                              return vName ? (
                                <p className="text-xs text-blue-600 font-medium truncate">Seller: {vName}</p>
                              ) : null;
                            })()}
                          </div>
                          
                          <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                            {listing.description ? listing.description.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').substring(0, 150) : ''}
                          </p>
                          
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              {(() => {
                                const displayPrice = getDisplayPrice(listing);
                                const displayMrp = getDisplayMrp(listing);
                                if (!displayPrice) return null;
                                return (
                                  <span className="flex items-baseline gap-2">
                                    <span className="text-sm md:text-base font-bold">{formatPrice(displayPrice)}</span>
                                    {displayMrp > displayPrice && (
                                      <span className="text-xs line-through text-gray-400">{formatPrice(displayMrp)}</span>
                                    )}
                                  </span>
                                );
                              })()}
                              {(listing.listing_type === 'product' || listing.data?.inventory) && (
                                <span className="text-xs text-gray-500">
                                  Stock: {getTotalInventory(listing)}
                                </span>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button 
                                variant="outline" 
                                size="sm"
                                title="Duplicate listing"
                                data-testid={`duplicate-listing-list-${listing.id}`}
                                onClick={(e) => handleDuplicate(listing, e)}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  navigate(getTypeRoute(listing.listing_type, listing.id, 'edit')); 
                                }}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  handleDelete(listing.id); 
                                }}
                              >
                                <Trash2 className="h-3 w-3 text-red-600" />
                              </Button>
                            </div>
                          </div>
                          
                          {listing.category && (
                            <div className="mt-2 flex gap-2 items-center">
                              <Badge variant="outline" className="text-xs">
                                {listing.category}
                              </Badge>
                              {getComplianceGap(listing).length > 0 && (
                                <Badge
                                  className="bg-red-100 text-red-800 text-xs"
                                  data-testid={`compliance-gap-badge-list-${listing.id}`}
                                  title={`Missing: ${getComplianceGap(listing).join(', ')}`}
                                >
                                  Needs Compliance Info
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              }) : (
                <Card>
                  <CardContent className="p-6 text-center text-gray-500">
                    No listings found
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>

        {/* Hidden file input for import */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImportWithPreview}
          accept=".csv"
          className="hidden"
        />

        {/* Type Selection Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Select Listing Type</DialogTitle>
              <DialogDescription>
                Choose the type of listing for this action
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LISTING_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleDialogConfirm}>
                Continue
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* HSN/SAC Alert Dialog */}
        <AlertDialog open={hsnAlertOpen} onOpenChange={setHsnAlertOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-amber-600">
                ⚠️ Important: Tax Code Management Required
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <p>
                  You already have <strong>{getExistingListingTypes().join(', ')}</strong> listings. 
                  You are now adding a <strong>{pendingListingType}</strong> listing.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
                  <p className="font-medium text-amber-800 mb-2">Please ensure you manage your HSN/SAC codes correctly:</p>
                  <ul className="list-disc list-inside text-amber-700 space-y-1">
                    <li><strong>Products</strong> require <strong>HSN codes</strong> (Harmonized System of Nomenclature)</li>
                    <li><strong>Services</strong> require <strong>SAC codes</strong> (Services Accounting Code)</li>
                    <li>Different listing types may have different tax implications</li>
                  </ul>
                </div>
                <p className="text-sm text-gray-600">
                  Make sure to update your tax settings in <strong>Settings → Tax Configuration</strong> if needed.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleHsnAlertCancel}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleHsnAlertConfirm} className="bg-amber-600 hover:bg-amber-700">
                I Understand, Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Edit Dialog */}
        <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
          <DialogContent className="max-w-md" data-testid="bulk-edit-dialog">
            <DialogHeader>
              <DialogTitle>Bulk Edit — {selectedListings.size} listing(s)</DialogTitle>
              <DialogDescription>
                Only filled fields will be updated. Leave blank to skip.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-sm font-medium">Category</Label>
                <Select value={bulkEditData.category} onValueChange={v => setBulkEditData(d => ({ ...d, category: v, sub_category: '' }))}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {getFilteredCategories().map(c => <SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {bulkEditData.category && getSelectedSubCategories(bulkEditData.category).length > 0 && (
                <div>
                  <Label className="text-sm font-medium">Sub-category</Label>
                  <Select value={bulkEditData.sub_category} onValueChange={v => setBulkEditData(d => ({ ...d, sub_category: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select sub-category" /></SelectTrigger>
                    <SelectContent>
                      {getSelectedSubCategories(bulkEditData.category).map(s => <SelectItem key={s.id || s.name} value={s.name}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isAdmin && (
                <>
                  <div>
                    <Label className="text-sm font-medium">Status</Label>
                    <Select value={bulkEditData.status} onValueChange={v => setBulkEditData(d => ({ ...d, status: v }))}>
                      <SelectTrigger><SelectValue placeholder="No change" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Assign Vendor (Store)</Label>
                    <Select value={bulkEditData.vendor_id} onValueChange={v => setBulkEditData(d => ({ ...d, vendor_id: v }))}>
                      <SelectTrigger><SelectValue placeholder="No change" /></SelectTrigger>
                      <SelectContent>
                        {vendors.map(v => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.vendor_profile?.store_name || v.vendor_profile?.business_details?.business_name || v.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              {!isAdmin && (
                <div>
                  <Label className="text-sm font-medium">Tags (comma separated)</Label>
                  <Input
                    placeholder="e.g. new-arrival, featured"
                    value={bulkEditData.tags}
                    onChange={e => setBulkEditData(d => ({ ...d, tags: e.target.value }))}
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setBulkEditOpen(false)}>Cancel</Button>
              <Button onClick={handleBulkEdit} disabled={bulkActionLoading} data-testid="bulk-edit-confirm">
                {bulkActionLoading ? 'Updating...' : 'Update All'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Import Preview Dialog */}
        <Dialog open={importPreviewOpen} onOpenChange={setImportPreviewOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="import-preview-dialog">
            <DialogHeader>
              <DialogTitle>
                Import Preview — {importPreviewData.items.length} item(s) 
                <Badge variant="outline" className="ml-2 text-xs">{importPreviewData.format}</Badge>
              </DialogTitle>
              <DialogDescription>
                Review items before importing. Uncheck items you want to skip.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
              <div>
                <Label className="text-sm font-medium">Category (optional)</Label>
                <Select value={importCategory} onValueChange={v => { setImportCategory(v); setImportSubCategory(''); }}>
                  <SelectTrigger><SelectValue placeholder="Keep from CSV" /></SelectTrigger>
                  <SelectContent>
                    {getFilteredCategories().map(c => <SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {importCategory && getSelectedSubCategories(importCategory).length > 0 && (
                <div>
                  <Label className="text-sm font-medium">Sub-category</Label>
                  <Select value={importSubCategory} onValueChange={setImportSubCategory}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {getSelectedSubCategories(importCategory).map(s => (
                        <SelectItem key={s.id || s.name} value={s.name}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isAdmin && (
                <div>
                  <Label className="text-sm font-medium">Assign to Vendor (Store)</Label>
                  <Select value={importVendorId} onValueChange={setImportVendorId}>
                    <SelectTrigger><SelectValue placeholder="No assignment" /></SelectTrigger>
                    <SelectContent>
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.vendor_profile?.store_name || v.vendor_profile?.business_details?.business_name || v.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left w-10">
                      <Checkbox
                        checked={importPreviewData.items.every(i => i.selected)}
                        onCheckedChange={(checked) => setImportPreviewData(prev => ({
                          ...prev,
                          items: prev.items.map(i => ({ ...i, selected: !!checked })),
                        }))}
                      />
                    </th>
                    <th className="px-3 py-2 text-left">Image</th>
                    <th className="px-3 py-2 text-left">Title</th>
                    <th className="px-3 py-2 text-left">Category</th>
                    <th className="px-3 py-2 text-left">Price</th>
                    <th className="px-3 py-2 text-left">SKU</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {importPreviewData.items.map((item, idx) => (
                    <tr key={item.id || item.name || `${item.title || "item"}-${idx}`} className={`${item.selected ? '' : 'opacity-40'} hover:bg-gray-50`}>
                      <td className="px-3 py-2">
                        <Checkbox checked={item.selected} onCheckedChange={() => togglePreviewItem(idx)} />
                      </td>
                      <td className="px-3 py-2">
                        {item.image ? (
                          <img src={item.image} alt="" className="w-10 h-10 object-cover rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                            <Package className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-medium max-w-[200px] truncate">{item.title}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{item.category || '—'}</td>
                      <td className="px-3 py-2">{item.price || '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{item.sku || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center pt-2">
              <p className="text-sm text-gray-500">
                {importPreviewData.items.filter(i => i.selected).length} of {importPreviewData.items.length} selected
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setImportPreviewOpen(false)}>Cancel</Button>
                <Button
                  onClick={confirmImport}
                  disabled={importing || importPreviewData.items.filter(i => i.selected).length === 0}
                  data-testid="import-confirm-btn"
                >
                  {importing ? 'Importing...' : `Import ${importPreviewData.items.filter(i => i.selected).length} Items`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ListingsUnified;
